import { Input, Select } from "@softmax/design-system";
import { fontSizes, spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useId, type ReactNode } from "react";
import type { CoworldOption, PrivateRunDraft } from "./coworld-records";
import { coworldLayout } from "./coworld-layout.styles";
import { CoworldRunRoster } from "./coworld-run-roster";

export function CoworldRunFields({
  draft,
  variants,
  bounds,
  onChange,
  playerPickers,
  disabled,
}: Readonly<{
  draft: PrivateRunDraft;
  variants: readonly CoworldOption[];
  bounds: Readonly<{ min: number; max: number }>;
  onChange(changes: Partial<PrivateRunDraft>): void;
  playerPickers: readonly ReactNode[];
  disabled: boolean;
}>) {
  const id = useId();
  return (
    <fieldset disabled={disabled} {...stylex.props(styles.fieldset)}>
      <div {...stylex.props(styles.field)}>
        <span>Variant</span>
        <Select
          aria-label="Run variant"
          value={draft.variant}
          options={variants}
          onValueChange={(variant) => onChange({ variant })}
        />
      </div>
      <div {...stylex.props(styles.fields)}>
        <label htmlFor={`${id}-players`} {...stylex.props(styles.field)}>
          Players
          <Input
            type="number"
            id={`${id}-players`}
            aria-label="Player count"
            min={bounds.min}
            max={bounds.max}
            required
            value={draft.playerCount}
            onChange={(event) =>
              onChange({ playerCount: event.target.valueAsNumber })
            }
          />
        </label>
        <label htmlFor={`${id}-episodes`} {...stylex.props(styles.field)}>
          Games
          <Input
            type="number"
            id={`${id}-episodes`}
            min={1}
            max={100}
            required
            value={draft.episodes}
            onChange={(event) =>
              onChange({ episodes: event.target.valueAsNumber })
            }
          />
        </label>
      </div>
      <CoworldRunRoster roster={draft.roster} playerPickers={playerPickers} />
      <label htmlFor={`${id}-spend`} {...stylex.props(styles.field)}>
        LLM spend limit per player, per game (USD)
        <Input
          type="number"
          id={`${id}-spend`}
          min={0}
          step="0.01"
          required
          value={draft.spendLimitUsd}
          onChange={(event) =>
            onChange({ spendLimitUsd: event.target.valueAsNumber })
          }
        />
      </label>
      <p {...stylex.props(coworldLayout.muted)}>
        Runs are private to you and the Softmax team. Running does not publish
        your Coworld.
      </p>
    </fieldset>
  );
}

const styles = stylex.create({
  fields: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: spacing.space16,
  },
  field: {
    display: "grid",
    gap: spacing.space8,
    fontSize: fontSizes.small,
    minWidth: 0,
  },
  fieldset: {
    borderWidth: 0,
    margin: 0,
    padding: 0,
    minWidth: 0,
    display: "grid",
    gap: spacing.space24,
  },
});
